import LanguageIcon from '@mui/icons-material/Language'
import PopupState, { bindTrigger, bindPopover } from 'material-ui-popup-state'
import Popover from '@mui/material/Popover'
import Button from '@mui/material/Button'
import { Box, Theme, Typography, useMediaQuery } from '@mui/material'
import { FC, useState } from 'react'

type OnboardingLanguagePickerProps = {
  onSelect?: (language: string) => void
}

const languages = [
  { code: 'en', name: 'English', flag: 'us' },
  { code: 'es', name: 'Spanish', flag: 'es' },
  { code: 'pt-br', name: 'Português', flag: 'br' },
]

const OnboardingLanguagePicker: FC<OnboardingLanguagePickerProps> = ({ onSelect }) => {
  const [lang, setLang] = useState(
    () => localStorage.getItem('weglot_current_lang') || 'en'
  )
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))

  const currentLanguage = languages.find(l => l.code === lang) ?? languages[0]

  const handleLanguageChange = (newLang: string) => {
    localStorage.setItem('weglot_current_lang', newLang)
    setLang(newLang)
    if (onSelect) {
      onSelect(newLang)
    }
    if ((window as any).Weglot) {
      (window as any).Weglot.switchTo(newLang)
      setTimeout(() => window.location.reload(), 100)
    }
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: isMobile ? 10 : 16,
        right: isMobile ? 10 : 16,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <PopupState variant="popover" popupId="language-switcher-popup">
        {(popupState) => (
          <div>
            <Button
              {...bindTrigger(popupState)}
              data-wg-notranslate
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                backgroundColor: 'white',
                padding: 1,
                borderRadius: 2,
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: 'grey.100',
                },
              }}
            >
              <img
                src={`https://flagcdn.com/w40/${currentLanguage.flag}.png`}
                alt={`${currentLanguage.name} flag`}
                width="20"
                height="15"
              />
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, textTransform: 'capitalize' }}
              >
                {currentLanguage.name}
              </Typography>
              <LanguageIcon fontSize="small" />
            </Button>
            <Popover
              {...bindPopover(popupState)}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
            >
              <Box
                data-wg-notranslate
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  padding: 1,
                  gap: 1,
                }}
              >
                {languages.map(language => (
                  <Button
                    key={language.code}
                    onClick={() => {
                      handleLanguageChange(language.code)
                      popupState.close()
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      justifyContent: 'flex-start',
                    }}
                  >
                    <img
                      src={`https://flagcdn.com/w40/${language.flag}.png`}
                      alt={`${language.name} flag`}
                      width="20"
                      height="15"
                    />
                    <Typography>{language.name}</Typography>
                  </Button>
                ))}
              </Box>
            </Popover>
          </div>
        )}
      </PopupState>
    </Box>
  )
}

export default OnboardingLanguagePicker
